import createClient from "openapi-fetch";

import type { paths } from "@/api/generated/openapi";
import { API_BASE_URL } from "@/config";
import { appVersion } from "@/lib/appVersion";
import { refusalFromBody, showLimitPrompt } from "@/lib/plans/limitPrompt";

/**
 * Supplies a Clerk session token to the API client. `forceRefresh` asks Clerk to bypass its token
 * cache and mint a new one, which is the recovery path a `401` takes (see {@link recoverFrom401}).
 * Resolves to `null` when Clerk has no live session, and may throw when Clerk itself is unreachable
 * (`ClerkOfflineError`, a 5xx, a rate limit). Only `null` means "signed out".
 */
export type AuthTokenGetter = (opts?: { forceRefresh?: boolean; }) => Promise<string | null>;

// The root layout, which holds the Clerk hooks, wires these in. Middleware is not a React context,
// so a token getter and a session-revoked callback are the seam between Clerk and the API client.
// The policy for what a 401 means stays in this module, not the layout, so it is unit-testable
// without mounting the authenticated tree.
let getAuthToken: AuthTokenGetter | null = null;
let onSessionRevoked: (() => void) | null = null;
let inFlightRefresh: Promise<string | null> | null = null;

// Every bridge that has installed a getter or handler and not yet cleared it, newest last; the
// newest is the live one. Two bridges can be mounted at once (the vendor app stays mounted beneath
// a storefront the app opened, whose follow control mounts its own), so the one that clears first
// must hand the slot back to the other rather than leave the app unable to sign a request.
const installedGetters: AuthTokenGetter[] = [];
const installedRevokedHandlers: Array<() => void> = [];

const authBridgeListeners = new Set<() => void>();

function applyGetter(next: AuthTokenGetter | null): void {
  if (next === getAuthToken) return;
  const wasReady = getAuthToken !== null;
  getAuthToken = next;
  inFlightRefresh = null; // A getter swap (sign-in or sign-out) invalidates any refresh in flight.
  if (wasReady !== (next !== null)) authBridgeListeners.forEach((notify) => notify());
}

const without = <T>(list: T[], item: T) => {
  const at = list.indexOf(item);
  if (at >= 0) list.splice(at, 1);
};

/** Installs `getter` as the live one; `null` drops every installed getter. */
export function setAuthTokenGetter(getter: AuthTokenGetter | null): void {
  if (getter === null) installedGetters.length = 0;
  else {
    without(installedGetters, getter);
    installedGetters.push(getter);
  }
  applyGetter(installedGetters.at(-1) ?? null);
}

/**
 * Whether a token getter is wired, that is, whether an authenticated request can carry a credential
 * at all. Check it before sending one (`useAuthBridgeReady`, `app/(app)/_layout.tsx`).
 *
 * This is not the same as "is there a session". The bridge is installed from an effect, which
 * commits after the first render, so Clerk can report a live session while this module has nothing
 * to sign with. A request sent in that window returns `401 missing bearer token` and cannot
 * recover, because `fetchWithAuthRetry` skips its retry when there is no getter. A request that
 * cannot be authenticated should not be sent (TRO-526).
 */
export function isAuthBridgeReady(): boolean {
  return getAuthToken !== null;
}

/** Subscribe to {@link isAuthBridgeReady} changes. Returns the unsubscribe. */
export function subscribeToAuthBridge(listener: () => void): () => void {
  authBridgeListeners.add(listener);
  return () => {
    authBridgeListeners.delete(listener);
  };
}

/**
 * Registers the hard sign-out. It is called only when Clerk itself reports the session is gone,
 * never on a bare 401 (#659).
 */
export function setSessionRevokedHandler(handler: (() => void) | null): void {
  if (handler === null) installedRevokedHandlers.length = 0;
  else {
    without(installedRevokedHandlers, handler);
    installedRevokedHandlers.push(handler);
  }
  onSessionRevoked = installedRevokedHandlers.at(-1) ?? null;
}

/**
 * Unwires `installed`, and only it. `AuthTokenBridge` can mount in two places at once (the vendor
 * app, and the follow control on a public storefront), so clearing the live getter hands the slot
 * back to the bridge installed before it, and clearing a superseded one leaves the live one alone.
 */
export function clearAuthTokenGetter(installed: AuthTokenGetter | null): void {
  if (installed === null) return;
  without(installedGetters, installed);
  applyGetter(installedGetters.at(-1) ?? null);
}

/** {@link clearAuthTokenGetter}'s counterpart for the session-revoked handler. */
export function clearSessionRevokedHandler(installed: (() => void) | null): void {
  if (installed === null) return;
  without(installedRevokedHandlers, installed);
  onSessionRevoked = installedRevokedHandlers.at(-1) ?? null;
}

/**
 * Force-mint a fresh token, collapsing concurrent callers onto one Clerk refresh. When a token
 * expires, every request in flight 401s at once; without this, each would fire its own
 * cache-skipping `getToken` and push Clerk into its rate limit, which this module treats as "not a
 * sign-out".
 */
function refreshAuthToken(getter: AuthTokenGetter): Promise<string | null> {
  if (!inFlightRefresh) {
    const pending: Promise<string | null> = getter({ forceRefresh: true }).finally(() => {
      // Clear only if this refresh is still the current one. An abandoned refresh (its getter was
      // swapped mid-flight, which resets the slot) that settles later would otherwise clear a newer
      // refresh's slot and let the next 401 start a third one.
      if (inFlightRefresh === pending) inFlightRefresh = null;
    });
    inFlightRefresh = pending;
  }
  return inFlightRefresh;
}

/**
 * Decide what a `401` means, and return the token to retry the request with, or `null` to let the
 * `401` stand. A single 401 is rarely a dead session (#659); it is usually a transient race against
 * Clerk's short-lived (~60s) session tokens.
 *
 * | forced refresh yields | meaning                                         | outcome          |
 * | --------------------- | ----------------------------------------------- | ---------------- |
 * | a token               | a live session exists; the sent token was stale | retry once       |
 * | `null`                | Clerk has no live session                       | sign out         |
 * | a throw               | Clerk is unreachable, offline or rate-limited   | give up, stay in |
 *
 * Only the `null` row is evidence of a revoked session, so only it signs the user out. A throw
 * keeps the user signed in and lets the failure surface as an ordinary request error.
 *
 * Does not compare the new token with the one that just 401'd. `skipCache` makes Clerk mint a fresh
 * JWT (new `iat`/`exp`) on almost every call, so "same string back" barely occurs, and
 * short-circuiting on it would wrongly skip the retry for a request that shared another request's
 * in-flight refresh. The call site bounds the retry instead.
 */
async function recoverFrom401(): Promise<string | null> {
  const getter = getAuthToken;
  const revoked = onSessionRevoked;
  // No token getter means no signed-in session is wired (public routes, or a sign-out already in
  // flight). There is nothing to refresh or revoke, so the 401 is correct.
  if (!getter) return null;
  let fresh: string | null;
  try {
    fresh = await refreshAuthToken(getter);
  } catch {
    return null;
  }
  // A getter swap cannot cancel a refresh in flight, so this verdict may describe a session that is
  // no longer current, and signing out on it would sign out whoever is signed in now (#659).
  // Skipping the sign-out is self-correcting: if the live session is gone, its own next 401
  // refreshes against the current getter and revokes then. The retry token is still returned,
  // because replaying with a token no older than the request's own is harmless and keeps recovery
  // working when the bridge is re-registered.
  if (fresh === null && getAuthToken === getter) revoked?.();
  return fresh;
}

const bearer = (token: string) => `Bearer ${token}`;

/**
 * How long to wait for Clerk to return a token before giving up (TRO-528).
 *
 * A ceiling on a pathological stall, not a latency budget. A healthy `getToken()` returns in
 * milliseconds, cached or not.
 */
const TOKEN_ACQUISITION_TIMEOUT_MS = 10_000;

/**
 * `getAuthToken()` bounded in time. Unbounded, a stalled getter is a request that never leaves,
 * because the middleware below cannot build the headers until it resolves. Clerk's `getToken()` can
 * stop resolving while `setActive` re-scopes a session to another org, which leaves
 * `GET /api/v1/me` pending with no success or failure and the workspace spinner with no end.
 *
 * Timing out yields `null`, the same shape as "no session": the request goes out unauthenticated,
 * returns 401, and takes the ordinary recovery path of a forced refresh and one retry.
 */
async function tokenWithTimeout(getter: AuthTokenGetter): Promise<string | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      getter(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), TOKEN_ACQUISITION_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * A request that got no HTTP response: offline, DNS, a failed TLS handshake, a connection dropped
 * mid-send. `fetch` rejects these with platform text that screens would otherwise render as-is,
 * such as expo/fetch's "fetch failed: UnexpectedException: A TLS error caused the secure connection
 * to fail. (at ExpoModulesCore/Promise.swift:56)". The platform error stays on `cause`.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Couldn't reach Trovie — check your connection and try again", { cause });
    this.name = "NetworkError";
  }
}

/**
 * `fetch`, with a transport failure rethrown as a {@link NetworkError}. An abort passes through
 * untouched, because it is the caller's own timeout or cancel and the caller words it. expo/fetch
 * rejects an abort as a plain `FetchError`, so only the signal tells the two apart.
 */
async function send(input: Request | string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (e) {
    const signal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
    if (signal?.aborted) throw e;
    throw new NetworkError(e);
  }
}

/**
 * The typed client's transport: send, and on a `401` recover once. The request is cloned before it
 * goes out, because `fetch` consumes a `Request`'s body. Exactly one retry per call, so a rejected
 * credential cannot loop.
 */
async function fetchWithAuthRetry(request: Request): Promise<Response> {
  // Buffer a copy only when a session could produce a better token; public and signed-out callers
  // pay nothing.
  const retryable = getAuthToken ? request.clone() : null;
  const response = await send(request);
  if (response.status !== 401 || !retryable) return response;
  const fresh = await recoverFrom401();
  if (fresh === null) return response;
  // Nothing reads the superseded 401, so release its body rather than leave the stream pinned.
  void response.body?.cancel().catch(() => {});
  retryable.headers.set("Authorization", bearer(fresh));
  return send(retryable);
}

/**
 * Typed API client generated from the backend's OpenAPI schema. Regenerate the types with
 * `npm run codegen:fetch` whenever the backend contract changes, so the app and the API cannot
 * drift.
 */
export const api = createClient<paths>({ baseUrl: API_BASE_URL, fetch: fetchWithAuthRetry });

api.use({
  async onRequest({ request }) {
    const { version, build } = appVersion();
    if (version) request.headers.set("X-Trovie-App-Version", `${version} (${build ?? "unknown"})`);
    const token = getAuthToken ? await tokenWithTimeout(getAuthToken) : null;
    if (token) request.headers.set("Authorization", bearer(token));
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status === 402 && request.method !== "GET") {
      const refusal = refusalFromBody(await response.clone().json().catch(() => null));
      if (refusal) showLimitPrompt(refusal);
    }
    // Return nothing: openapi-fetch rejects a returned response that is not `instanceof Response`,
    // and expo/fetch's native response is not (TRO-2521).
  },
});

/** `RequestInit` narrowed to the plain-object header form every {@link authedFetch} caller uses. */
export type AuthedInit = Omit<RequestInit, "headers"> & { headers?: Record<string, string>; };

/**
 * `fetch` with the bearer token attached and the same `401` recovery as the typed client, for the
 * binary bodies that cannot go through it: multipart uploads (`@/lib/scan/identify`,
 * `@/lib/inventory/imports`) and the PDF download (`@/lib/labels/exportLabels`).
 *
 * Retries by re-issuing the call with the caller's `init` rather than cloning a `Request`, so
 * `init.body` must be replayable. A `FormData` (whose file parts come from
 * `@/lib/platform/filePart`), string or blob re-serializes on each send; a one-shot
 * `ReadableStream` would arrive empty on the retry. An `init.signal` spans both attempts, so a
 * caller's timeout caps the whole operation, and a timeout that fires mid-recovery surfaces as the
 * caller's abort rather than as the 401.
 */
export async function authedFetch(url: string, init: AuthedInit = {}): Promise<Response> {
  const token = getAuthToken ? await tokenWithTimeout(getAuthToken) : null;
  const sendWith = (t: string | null) => {
    const { version, build } = appVersion();
    return send(url, {
      ...init,
      headers: {
        ...init.headers,
        ...(version && { "X-Trovie-App-Version": `${version} (${build ?? "unknown"})` }),
        ...(t && { Authorization: bearer(t) }),
      },
    });
  };
  let response = await sendWith(token);
  if (response.status === 401) {
    const fresh = await recoverFrom401();
    if (fresh !== null) {
      void response.body?.cancel().catch(() => {});
      response = await sendWith(fresh);
    }
  }
  if (response.status === 402 && init.method !== "GET") {
    const refusal = refusalFromBody(await response.clone().json().catch(() => null));
    if (refusal) showLimitPrompt(refusal);
  }
  return response;
}
